import { ajaxActions } from '../../Service/AjaxActions';
import { DataS } from '../../Service/DataS';
import { fetchResourceByEntityTypeAndEntityId } from '../../Service/restapi/shareService';
import { ToastS } from '../../Service/ToastS';
import { PageableResponse } from '../../Types/PageableResponse';
import { ResourcePosition } from '../../Organismns/Settings/Documents/Positions/ResourcePositionAssignment';
import { fetchContractById } from '../../Service/restapi/contractService';
import { QuestionnaireS } from '../../Service/QuestionnaireS';
import BookitupDocument, { BookitupContract, BookitupDocumentType, DocumentFilter } from '../../Types/BookitupDocument';
import { DocumentDto, PositionDto, SequentialNumber } from '../../Service/DocumentT';
import { FormatS } from '../../Service/FormatS';
import { DocumentBlueprintDto, DocumentRequestDTO, DocumentTemplateDTO } from '../../Types/MessageT';
import { ContractDto, CreateContractRequest } from '../../Types/DocumentT';
import { isDefined } from '../../Service/ValidationS';
import { FileS } from '../../Service/FileS';
import DocumentLanguageS from '../../Service/DocumentLanguageS';

type DiscountType = 'ABSOLUTE' | 'PERCENT';

export type TaxType = 'gross' | 'net';

export type Discount = {
  value: number;
  type: DiscountType;
  description: string;
  amount: number;
};

const EMPTY_DOCUMENT = {
  id: null,
  draft: true,
  bookingState: 'OPEN',
  summable: true,
  customer: {},
  subject: 'OFFER',
  documentType: 'OFFER',
  number: '',
  daysTilExpire: '',
  headrow: '',
  positions: [{ value: '', label: '', subtext: '', price: 0, favorite: false, id: null }],
  textBlock: '',
  textTemplateDescription: '',
  endrow: '',
};

const initDocument = (contactId: number, offerValidUntil: Date | null) => {
  const date = new Date();
  return {
    ...EMPTY_DOCUMENT,
    date,
    contactId,
    validUntil: offerValidUntil || date,
  };
};

const BASE_URL = process.env.REACT_APP_DOCUMENT_SERVICE_URL;

const fetchTemplate = async (type: BookitupDocumentType, id: number) => {
  const resp = await ajaxActions.get(`${BASE_URL}/report/template/${type.toUpperCase()}/${id}`);
  if (resp.ok) {
    return resp.json();
  }
  ToastS.generalError();
  return null;
};

const loadTemplates = (documentType: BookitupDocumentType): Promise<string[]> =>
  ajaxActions.get(`${BASE_URL}/report/template/${documentType}/names`).then((resp) => {
    if (resp.ok) {
      return resp.json();
    }
    // eslint-disable-next-line no-console
    console.error(`Unable to load templates per documentType: ${documentType}`);
    return [];
  });

type PartialInvoiceDTO = {
  amount: number;
  amountUnit: string;
};

const createPartialInvoice = async (
  origin: number,
  customerId: number,
  dto: PartialInvoiceDTO,
  templateName?: string,
) => {
  let targetUrl = `${BASE_URL}/documents/${origin}/partial-invoice?customerId=${customerId}`;
  if (templateName) targetUrl += `&templateName=${templateName}`;
  const resp = await ajaxActions.put(targetUrl, dto);
  if (resp.status === 201) {
    return resp.json();
  }
  return null;
};

const getUnassignedPartialInvoices = async (eventId: number) => {
  const resp = await ajaxActions.get(`${BASE_URL}/documents/partial-invoice/unassigned?eventId=${eventId}`);
  if (resp.ok) {
    return resp.json();
  }
  return null;
};

const patchDocument = async (
  documentId: number,
  patch: Partial<DocumentDto>,
  interceptViolation?: (httpResponse: Response) => void,
) => {
  const resp = await ajaxActions.patch(`${BASE_URL}/documents/${documentId}`, patch);
  if (resp.ok) {
    return resp.json();
  }
  if (interceptViolation) {
    interceptViolation(resp);
  }
  return null;
};

const acceptOffer = async (documentId: number, acceptedPositions: number[]): Promise<DocumentDto | null> => {
  const resp = await ajaxActions.post(`${BASE_URL}/documents/${documentId}/accept`, acceptedPositions);
  if (resp.ok) {
    return resp.json();
  }
  ToastS.generalError();
  return null;
};

const copyDocument = async (
  documentId: number,
  documentType?: string,
  templateName?: string,
  customerId?: number,
  finalInvoice?: boolean,
  interceptViolation?: (httpResponse: Response) => void,
): Promise<BookitupDocument | null> => {
  let requestParam = documentType ? `?documentType=${documentType}` : '';
  requestParam += templateName ? `&templateName=${templateName}` : '';
  requestParam += customerId ? `&customerId=${customerId}` : '';
  requestParam += finalInvoice ? `&finalInvoice=true` : '';

  const resp = await ajaxActions.put(`${BASE_URL}/documents/${documentId}${requestParam}`);
  if (resp.ok) {
    return resp.json();
  }
  if (interceptViolation) {
    interceptViolation(resp);
  } else {
    ToastS.error('err', 'Dokument kann derzeit nicht erstellt werden. Bitte versuch es später.');
  }

  return null;
};

const patchContract = async (contractId: number, patch: Partial<BookitupContract>) => {
  const resp = await ajaxActions.patch(`${BASE_URL}/contracts/${contractId}`, patch);
  if (resp.ok) {
    return resp.json();
  }
  return null;
};

const fetchById = async (id: number): Promise<DocumentDto | null> => {
  const resp = await ajaxActions.get(`${BASE_URL}/documents/${id}`);
  if (resp.ok) {
    return resp.json();
  }
  return null;
};

const fetchResourcePositions = async (resourceId: string): Promise<ResourcePosition[]> => {
  const resp = await ajaxActions.get(`${BASE_URL}/resourcePositions?resourceId=${resourceId}`);
  if (resp.ok) {
    return resp.json();
  }
  ToastS.generalError();
  return Promise.reject();
};

const fetchResourcePositionFavorites = async (resourceId: string): Promise<ResourcePosition[]> => {
  const resp = await ajaxActions.get(`${BASE_URL}/resourcePositionFavorites?resourceId=${resourceId}`);
  if (resp.ok) {
    return resp.json();
  }
  ToastS.generalError();
  return Promise.reject();
};

const isPublished = async (entityId: number, entityType: string): Promise<boolean> => {
  const resp = await fetchResourceByEntityTypeAndEntityId(entityType, entityId);
  if (resp.ok) {
    const data: [] = await resp.json();
    return data.length > 0;
  }
  return false;
};

// eslint-disable-next-line consistent-return
const deleteDocument = async (bookitupDocument: DocumentDto): Promise<boolean | undefined> => {
  const { id } = bookitupDocument;
  const isDocumentPublished = await isPublished(id, 'documents');

  if (isDocumentPublished) {
    ToastS.error('delete-document', 'Dokument kann nicht gelöscht werden, da es im Online-Portal freigegeben wurde.');
  } else if (cannotBeDeleted(bookitupDocument)) {
    const { paid, hasPayments } = bookitupDocument;
    // eslint-disable-next-line no-nested-ternary
    const toastMessage = hasPayments
      ? paid
        ? 'Dokument wurde festgeschrieben und kann nicht gelöscht werden'
        : 'Dokument kann nicht gelöscht werden, da bereits Zahlungen erfasst wurden'
      : 'Dokument kann nicht gelöscht werden, da es bereits fertiggestellt wurde';
    ToastS.error('delete-document', toastMessage);
  } else {
    const resp = await ajaxActions.del(`${BASE_URL}/documents/${id}`);
    if (resp.ok) {
      ToastS.info('delete.document', 'Dokument wurde gelöscht');
      return true;
    }
    ToastS.generalError();
    return false;
  }
};

const deleteAllDocuments = async () => {
  const resp = await ajaxActions.del(`${BASE_URL}/documents/all`);
  if (resp.ok) {
    ToastS.info('delete.documents', 'All offers removed');
    return;
  }
  ToastS.generalError();
};

const deleteContract = async (id: number): Promise<boolean> => {
  const isContractPublished = await isPublished(id, 'contracts');
  if (isContractPublished) {
    ToastS.error('delete-document', 'Dokument kann nicht gelöscht werden, da es im Online-Portal freigegeben wurde.');
    return false;
  }
  const resp = await ajaxActions.del(`${BASE_URL}/contracts/${id}`);
  if (resp.ok) {
    ToastS.info('delete.document', 'Dokument wurde gelöscht');
    return true;
  }
  ToastS.generalError();
  return false;
};

const deleteAllContracts = async () => {
  const resp = await ajaxActions.del(`${BASE_URL}/contracts/all`);
  if (resp.ok) {
    ToastS.info('delete.contracts', 'All contracts removed');
    return;
  }
  ToastS.generalError();
};

const cannotBeDeleted = (bookitupDocument: DocumentDto): boolean => {
  const { documentType, draft } = bookitupDocument;
  return !draft && (documentType === 'CANCEL' || isInvoice(documentType));
};

const isInvoice = (documentType: BookitupDocumentType): boolean =>
  documentType === 'INVOICE' || documentType === 'PARTIAL_INVOICE';

const fetchDocuments = async (
  filter: DocumentFilter,
  setEmptyDocuments: (val: boolean) => void,
  abortSignal: AbortSignal,
): Promise<PageableResponse<DocumentDto> | null> => {
  const params = DataS.removeEmptyValues(filter);
  const query = new URLSearchParams(params as any).toString();
  const resp = await ajaxActions.get(`${BASE_URL}/documents/filter?${query}`, { signal: abortSignal });
  if (resp.ok) {
    const totalCountHeader = resp.headers.get('X-Total-Count');
    if (totalCountHeader && totalCountHeader === '0') {
      setEmptyDocuments(true);
    } else {
      setEmptyDocuments(false);
    }
    return resp.json();
  }
  return null;
};

const fetchContracts = async (
  filter: DocumentFilter,
  setEmptyDocuments: (val: boolean) => void,
  abortSignal: AbortSignal,
): Promise<PageableResponse<BookitupContract> | null> => {
  const params = DataS.removeEmptyValues(filter);
  const query = new URLSearchParams(params as any).toString();
  const resp = await ajaxActions.get(`${BASE_URL}/contracts/filter?${query}`, { signal: abortSignal });
  if (resp.ok) {
    const totalCountHeader = resp.headers.get('X-Total-Count');
    if (totalCountHeader && totalCountHeader === '0') {
      setEmptyDocuments(true);
    } else {
      setEmptyDocuments(false);
    }
    return resp.json();
  }
  return null;
};

const filterContracts = async (documentId?: number, customerContactId?: number): Promise<BookitupContract[]> => {
  let targetUrl = `${BASE_URL}/contracts`;
  if (documentId) {
    targetUrl += `?documentId=${documentId}`;
  }
  if (customerContactId) {
    targetUrl += `?customerContactId=${customerContactId}`;
  }
  const resp = await ajaxActions.get(targetUrl);
  if (resp.ok) {
    return resp.json();
  }
  return [];
};

interface GroupedDocuments {
  [key: string]: DocumentDto[];
}

const groupDocumentsByType = (documents: DocumentDto[]): GroupedDocuments => {
  const groupedDocuments: GroupedDocuments = {};
  ['OFFER', 'CONFIRMATION', 'PARTIAL_INVOICE', 'INVOICE', 'CANCEL'].forEach((docType) => {
    const filteredDocs = documents.filter((d) => d.documentType === docType);
    if (filteredDocs.length > 0) {
      groupedDocuments[docType] = filteredDocs;
    }
  });
  return groupedDocuments;
};

const importProductsLOX = async (csvFile: File) => {
  ToastS.info(
    'importing-products',
    'Abhängig von der Anzahl Deiner Produkte/Service kann es einen Moment dauern.',
    'Produkte/Service importieren',
  );
  const res = await ajaxActions.uploadFile(`${BASE_URL}/import/products/lox`, csvFile);
  if (res.ok) {
    const importedProductsCount = await res.text();
    ToastS.success(
      'products-imported',
      `${importedProductsCount} Produkte erfolgreich importiert`,
      'Produkte/Service importiert',
    );
    return;
  }
  ToastS.generalError();
};

const importProductsSevdesk = async (csvFile: File) => {
  ToastS.info(
    'importing-products',
    'Abhängig von der Anzahl Deiner Produkte/Service kann es einen Moment dauern.',
    'Produkte/Service importieren',
  );
  const res = await ajaxActions.uploadFile(`${BASE_URL}/import/products/sevdesk`, csvFile);
  if (res.ok) {
    const importedProductsCount = await res.text();
    ToastS.success(
      'products-imported',
      `${importedProductsCount} Produkte erfolgreich importiert`,
      'Produkte/Service importiert',
    );
    return;
  }
  ToastS.generalError();
};

const getEntityFileInfo = async (entityId: string, entityType: string) => {
  let fileName;
  if (entityType === 'contracts') {
    const res = await fetchContractById(entityId);
    if (res.ok) {
      const resJSON = await res.json();
      fileName = resJSON.fileName;
    }
  }
  if (entityType === 'documents') {
    const _document = await DocumentS.fetchById(Number(entityId));
    if (_document) {
      fileName = _document.fileName;
    }
  }
  if (entityType === 'questionnaires') {
    const questionnaire = await QuestionnaireS.fetchQuestionnaire(entityId);
    if (questionnaire) {
      fileName = questionnaire.fileName;
    }
  }
  return {
    entityId,
    entityType,
    fileName,
  };
};

const exchangeExistingDocuments = async (
  eventId: string,
  documentRequests: DocumentBlueprintDto[],
): Promise<DocumentRequestDTO[]> => {
  const res = await ajaxActions.post(`${BASE_URL}/process/exchangeToExistingDocumentsIfPossible`, {
    eventId,
    documentRequests,
  });
  if (res.ok) {
    return res.json();
  }
  return [];
};

const stringifyBlueprints = (documentBlueprints: DocumentBlueprintDto[]) => {
  const dBArray: string[] = [];
  documentBlueprints.forEach((dB) => {
    if (dB.documentType === 'OFFER') {
      dBArray.push('ein Angebot');
    } else if (dB.documentType === 'CONFIRMATION') {
      dBArray.push('eine Auftragbestätigung');
    } else if (dB.documentType === 'PARTIAL_INVOICE') {
      dBArray.push('ein Abschlagsrechnung');
    } else if (dB.documentType === 'INVOICE') {
      dBArray.push('eine Rechnung');
    } else if (dB.documentType === 'CANCEL') {
      dBArray.push('eine Rechnungskorrektur');
    } else if (dB.documentType === 'CONTRACT') {
      dBArray.push('ein Vertrag');
    }
  });
  let output = dBArray[0];
  if (dBArray.length > 2) {
    output = dBArray.slice(0, dBArray.length - 1).join(', ');
    output += ` und ${dBArray[dBArray.length - 1]}`;
  } else if (dBArray.length > 1) {
    output = `${dBArray[0]} und ${dBArray[1]}`;
  }
  return output;
};

const movePosition = async (
  positionId: number,
  documentId: number,
  direction: 'UP' | 'DOWN',
): Promise<DocumentDto | null> => {
  const res = await ajaxActions.patch(
    `${BASE_URL}/positions/${positionId}/move/${direction}?documentId=${documentId}`,
    null,
  );
  if (res.ok) {
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const movePositionV2 = async (documentId: number, fromId: number, toId: number): Promise<DocumentDto | null> => {
  const res = await ajaxActions.patch(
    `${BASE_URL}/documents/v2/${documentId}/positions?fromId=${fromId}&toId=${toId}`,
    null,
  );
  if (res.ok) {
    return res.json();
  }
  return null;
};

const deletePositionV2 = async (documentId: number, positionId: number): Promise<DocumentDto | null> => {
  const res = await ajaxActions.del(`${BASE_URL}/documents/v2/${documentId}/positions?positionId=${positionId}`, null);
  if (res.ok) {
    ToastS.info('position-deleted', 'Position entfernt');
    return res.json();
  }
  return null;
};

const movePositionGroup = async (documentId: number, from: number, to: number): Promise<DocumentDto | null> => {
  const res = await ajaxActions.patch(
    `${BASE_URL}/documents/${documentId}/move/positionGroups?fromIndex=${from}&to=${to}`,
    null,
  );
  if (res.ok) {
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const insertPositionV2 = async (
  documentId: number,
  targetPositionId?: number,
  product?: PositionDto,
): Promise<DocumentDto | null> => {
  const res = await ajaxActions.put(
    `${BASE_URL}/documents/v2/${documentId}/positions${
      targetPositionId ? `?targetPositionId=${targetPositionId}` : ''
    }`,
    product,
  );
  if (res.ok) {
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const printify = (documentDto: DocumentDto) => {
  const { partialInvoices, number, documentType, language } = documentDto;
  const isFinalInvoice = partialInvoices && partialInvoices.length > 0 && documentType !== 'REMINDER';
  let str = DocumentLanguageS.documentType(language, documentType, isFinalInvoice);
  if (number) {
    str += ` Nr. ${number}`;
  }
  return str;
};

const sumUpGroup = (positions: PositionDto[], taxType: TaxType) => {
  const sum = positions.reduce((acc, p) => {
    acc += taxType === 'net' ? p.netPrice : p.price;
    return acc;
  }, 0);
  return FormatS.numberToCurrency(sum);
};

const swapPositions = async (
  documentId: number,
  direction: string,
  positionId: number,
): Promise<DocumentDto | null> => {
  const res = await ajaxActions.patch(
    `${BASE_URL}/documents/${documentId}/swap/${direction}?positionId=${positionId}`,
    null,
  );
  if (res.ok) {
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const getContractTemplatesNames = async (): Promise<string[]> => {
  const res = await ajaxActions.get(`${BASE_URL}/contracts/templates/names`);
  if (res.ok) {
    return res.json();
  }
  return [];
};

const createContract = async (req: CreateContractRequest): Promise<ContractDto | null> => {
  const requestParams = new URLSearchParams(req as any).toString();
  const res = await ajaxActions.put(`${BASE_URL}/contracts?${requestParams}`);
  if (res.ok) {
    ToastS.success('contract-created', 'Vertrag erstellt');
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const contractCreatable = (documentType: BookitupDocumentType) =>
  documentType === 'OFFER' || documentType === 'CONFIRMATION' || documentType === 'INVOICE';

const saveAsTemplate = async (templateName: string, documentId: number): Promise<DocumentTemplateDTO | null> => {
  const res = await ajaxActions.put(`${BASE_URL}/templates/${templateName}?documentId=${documentId}`);
  if (res.ok) {
    ToastS.info('template-created', 'Vorlage erstellt.');
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const getDocumentDescription = (document: DocumentDto) => {
  const { documentType, number, sum, language } = document;
  return `${DocumentLanguageS.documentType(language, documentType)} Nr. ${number} - ${sum} EUR`;
};

const resolveLabel = (bookitupDocument: DocumentDto) => {
  const { number, documentType, partialInvoices, label: documentLabel, language } = bookitupDocument;
  if (documentLabel === 'Zahlungserinnerung') {
    return documentLabel;
  }
  const isFinalInvoice = partialInvoices && partialInvoices.length > 0 && documentType !== 'REMINDER';
  let label = DocumentLanguageS.documentType(language, documentType, isFinalInvoice);

  if (isDefined(number)) {
    label += ` ${language === 'english' ? 'No' : 'Nr.'} ${number}`;
  }
  return label;
};

const editSequantialNumber = async (numberType: string, patch: Partial<SequentialNumber>) => {
  const res = await ajaxActions.patch(`${BASE_URL}/sequentialNumbers/${numberType}`, patch);
  if (res.ok) {
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const insertProducts = async (
  documentId: number,
  productIds: number[],
  targetPositionId?: number,
): Promise<DocumentDto | null> => {
  const res = await ajaxActions.put(
    `${BASE_URL}/documents/${documentId}/products${targetPositionId ? `?targetPositionId=${targetPositionId}` : ''}`,
    productIds,
  );
  if (res.ok) {
    ToastS.info('foo', 'Produkte eingefügt.');
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const fetchDocumentsByCustomerContactId = async (contactId: number): Promise<DocumentDto[]> => {
  const res = await ajaxActions.get(`${BASE_URL}/documents?customerContactId=${contactId}`);
  if (res.ok) {
    return res.json();
  }
  return [];
};

const personalizeTemplates = async (service: string) => {
  const res = await ajaxActions.get(`${BASE_URL}/templates/personalize?service=${service}`);
  if (res.ok) {
    return true;
  }
  return false;
};

const exportDocumentsCSV = async (
  filename: string,
  eventYear?: number,
  eventKinds?: string[],
  bookingStates?: string[],
  documentTypes?: string[],
) => {
  const yearParam = eventYear !== null && eventYear !== undefined ? `&eventYear=${eventYear}` : '';
  const eventKindsParam = eventKinds && eventKinds.length > 0 ? `&eventKinds=${eventKinds}` : '';
  const bookingStatesParam = bookingStates && bookingStates.length > 0 ? `&bookingStates=${bookingStates}` : '';
  const documentTypesParam = documentTypes && documentTypes.length > 0 ? `&documentTypes=${documentTypes}` : '';

  const res = await ajaxActions.get(
    `${process.env.REACT_APP_DOCUMENT_SERVICE_URL}/csv/export/documents?filename=${encodeURIComponent(
      filename,
    )}${yearParam}${eventKindsParam}${bookingStatesParam}${documentTypesParam}`,
  );

  if (res.ok) {
    const blob = await res.blob();
    FileS.download(filename, blob);
  } else {
    ToastS.generalError();
  }
};

// eslint-disable-next-line import/prefer-default-export
export const DocumentS = {
  initDocument,
  fetchTemplate,
  loadTemplates,
  createPartialInvoice,
  getUnassignedPartialInvoices,
  patchDocument,
  acceptOffer,
  copyDocument,
  fetchDocuments,
  fetchContracts,
  filterContracts,
  fetchResourcePositions,
  fetchResourcePositionFavorites,
  fetchById,
  patchContract,
  deleteDocument,
  deleteAllDocuments,
  isInvoice,
  groupDocumentsByType,
  deleteContract,
  deleteAllContracts,
  isPublished,
  importProductsLOX,
  importProductsSevdesk,
  getEntityFileInfo,
  exchangeExistingDocuments,
  stringifyBlueprints,
  movePosition,
  movePositionV2,
  deletePositionV2,
  movePositionGroup,
  insertPositionV2,
  printify,
  sumUpGroup,
  swapPositions,
  getContractTemplatesNames,
  createContract,
  contractCreatable,
  saveAsTemplate,
  getDocumentDescription,
  resolveLabel,
  editSequantialNumber,
  insertProducts,
  fetchDocumentsByCustomerContactId,
  personalizeTemplates,
  exportDocumentsCSV,
};
